import { StationAvatar } from '@app/components/base/stations/StationAvatar';
import { KeyValue } from '@app/core/components/KeyValue';
import { canEditStation } from '@app/core/stations/helpers';
import { useStationGroups } from '@app/core/stations/hooks/useStationGroups';
import { Station } from '@app/core/types/stations';
import { getLanguage } from '@mi-front/core';
import { Box, Chip } from '@mui/material';
import { QueryView } from '@ui/utils/async-view/QueryView';
import { FormattedMessage } from 'react-intl';
import { StationType } from './StationType';

interface Props {
  readonly station: Station;
}

const LOGO_SIZE = 120;

/**
 * Read-only station details: name, type, timezone, language, and groups,
 * with a large rounded-square logo on the right.
 */
export function StationInfo(p: Props) {
  const { station } = p;
  const isOwnerStation = canEditStation(station);
  const groupsQuery = useStationGroups(station.id, isOwnerStation);

  return (
    <Box display="flex" gap={4} alignItems="flex-start" justifyContent="space-between">
      <Box flex={1} minWidth={0}>
        <KeyValue label={<FormattedMessage id="common.text.name" defaultMessage="Nombre" />}>
          {station.name}
        </KeyValue>
        {station.type && (
          <KeyValue label={<FormattedMessage id="common.text.type" defaultMessage="Tipo" />}>
            <StationType type={station.type} />
          </KeyValue>
        )}
        <KeyValue
          label={<FormattedMessage id="common.text.timezone" defaultMessage="Zona horaria" />}
        >
          {station.timezone}
        </KeyValue>
        <KeyValue label={<FormattedMessage id="common.text.language" defaultMessage="Idioma" />}>
          {getLanguage(station.language)?.name}
        </KeyValue>
        {isOwnerStation && (
          <KeyValue
            label={
              <FormattedMessage id="admin.stations.viewScreen.groups" defaultMessage="Grupos" />
            }
          >
            <QueryView inline query={groupsQuery}>
              {(groups) => {
                if (!groups || groups.length === 0) {
                  return (
                    <FormattedMessage
                      id="admin.stations.viewScreen.noGroups"
                      defaultMessage="No se encontraron grupos"
                    />
                  );
                }

                return (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {groups.map((group) => (
                      <Chip key={group.id} label={group.name} variant="outlined" />
                    ))}
                  </Box>
                );
              }}
            </QueryView>
          </KeyValue>
        )}
      </Box>
      <KeyValue
        label={<FormattedMessage id="admin.stations.viewScreen.logo" defaultMessage="Logo" />}
      >
        <StationAvatar
          stationId={station.id}
          station={station}
          size={LOGO_SIZE}
          variant="rounded"
          sx={{ flexShrink: 0 }}
        />
      </KeyValue>
    </Box>
  );
}
